import { UiState } from 'instantsearch.js';
import { history } from 'instantsearch.js/es/lib/routers';
import type { RouterProps } from 'instantsearch.js/es/middlewares/createRouterMiddleware';
import { path } from 'ramda';
import { useMemo } from 'react';

import { UseSearchConfigMapperResult } from '../../hooks/useSearchConfigMapper/useSearchConfigMapper.ts';
import { useSearchDomains } from '../../hooks/useSearchDomains/useSearchDomains.ts';
import { FacetVariant } from '../../types/FacetVariant.ts';

export type RouteState = {
  query?: string;
};

type RoutingProps = RouterProps<UiState, RouteState>;

export type UseSearchRouterProps = UseSearchConfigMapperResult;

export interface UseSearchRouterResult {
  routing: RoutingProps;
}

export const useSearchRouter = ({
  searchConfigByDomain,
  sortByValueToSlug,
  sortBySlugToValue,
}: UseSearchRouterProps): UseSearchRouterResult => {
  const { searchDomains } = useSearchDomains();
  const routing = useMemo<RoutingProps>(() => {
    const router = history<RouteState>({
      cleanUrlOnDispose: false,

      createURL: ({ qsModule, routeState, location }) => {
        const { protocol, hostname, port = '', pathname, hash } = location;
        const queryString = qsModule.stringify(routeState, {
          addQueryPrefix: true,
          arrayFormat: 'comma',
          allowEmptyArrays: false,
          allowDots: true,
          encodeValuesOnly: true,
        });
        const portWithPrefix = port === '' ? '' : `:${port}`;

        // IE <= 11 has no proper `location.origin` so we cannot rely on it.
        if (!queryString) {
          return `${protocol}//${hostname}${portWithPrefix}${pathname}${hash}`;
        }

        return `${protocol}//${hostname}${portWithPrefix}${pathname}${queryString}${hash}`;
      },

      parseURL: ({ qsModule, location }) => {
        return qsModule.parse(location.search.slice(1), {
          // `qs` by default converts arrays with more than 20 items to an object.
          // We want to avoid this because the data structure manipulated can therefore vary.
          // Setting the limit to `100` seems a good number because the engine's default is 100
          // (it can go up to 1000 but it is very unlikely to select more than 100 items in the UI).
          //   Using an `arrayLimit` of `n` allows `n + 1` items.
          arrayLimit: 99,
          allowDots: true,
          parseArrays: true,
          comma: true,
        }) as unknown as RouteState;
      },
    });

    return {
      router,
      stateMapping: {
        stateToRoute(uiState) {
          const commonUiState = uiState[''];
          const routeState = {
            query: commonUiState.query,
            ...searchDomains.reduce((state, searchDomain) => {
              const searchConfig = searchConfigByDomain(searchDomain);
              const indexUiState = uiState[searchConfig.indexName] || {};
              state[searchConfig.slug] = {
                page: indexUiState.page,
                sort_by: sortByValueToSlug(searchDomain, indexUiState.sortBy),
                // also map facet attribute names
                ...searchConfig.filters.reduce(
                  (acc, { attribute, facetVariant, slug, valueStateToRoute: toRouteValue }) => {
                    const filterValue = path([facetVariant, attribute], indexUiState);
                    const routeValue = toRouteValue ? toRouteValue(filterValue) : filterValue;
                    if (routeValue) {
                      acc[slug] = routeValue;
                    }
                    return acc;
                  },
                  {} as any,
                ),
              };
              return state;
            }, {} as any),
          };
          return routeState;
        },

        routeToState(routeState: any = {}) {
          const uiState = {
            ['']: {
              query: routeState.query,
            },
            ...searchDomains.reduce((state, searchDomain) => {
              const searchConfig = searchConfigByDomain(searchDomain);
              const indexRouteState = routeState[searchConfig.slug] ?? {};

              state[searchConfig.indexName] = {
                sortBy: sortBySlugToValue(searchDomain, indexRouteState.sort_by),
                ...(indexRouteState.page ? { page: indexRouteState.page } : undefined),
                // also map facet attribute names
                ...searchConfig.filters.reduce((acc, { attribute, facetVariant, slug }) => {
                  let value = indexRouteState[slug];
                  if (value) {
                    if (facetVariant === FacetVariant.REFINEMENT_LIST && !Array.isArray(value)) {
                      value = [value];
                    }
                    acc[facetVariant] ||= {};
                    acc[facetVariant][attribute] = value;
                  }
                  return acc;
                }, {} as any),
              };

              return state;
            }, {} as any),
          };
          return uiState;
        },
      },
    } satisfies RoutingProps;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDomains]);

  return { routing };
};
